import { useMemo, useCallback, useState } from "react";
import { NumericFormat } from "react-number-format";
import ArrowDownSLineIcon from "remixicon-react/ArrowDownSLineIcon";
import ArrowUpSLineIcon from "remixicon-react/ArrowUpSLineIcon";
import Icon from "@mdi/react";
import { createUseStyles } from "$utils/createUseStyles";
import SentimentVeryDissatisfiedIcon from "@mui/icons-material/SentimentVeryDissatisfied";
import SentimentSatisfiedAltIcon from "@mui/icons-material/SentimentSatisfiedAlt";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdfOutlined";
import DeleteForeverOutlinedIcon from "@mui/icons-material/DeleteForeverOutlined";
import { mdiPackageVariantClosed } from "@mdi/js";

import {
  Button,
  CircularProgress,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
} from "@mui/material";
import FileDownload from "js-file-download";
import {
  PreparationOrder,
  Shipment,
  ShippingRecord,
  ShippingRecordStatus,
} from "$types";

import Text from "$components/Text";
import { Modal } from "$components";
import JTable from "$components/JTable/JTable";
import ParcelTable from "$containers/Parcel/ParcelTable";
import InfoContainer from "$components/InfoContainer";

import { PreparationOrderErrors } from "$utils/typeConstants";
import { exportPdf } from "$services/Public.service";
import useParcel from "$hooks/useParcel";
import useJStyles from "$hooks/useJStyles";

function PDFButton({ opData }: any) {
  const { parcel } = useParcel(opData?._id);
  const classes = useStyles();

  const generatePdf = useCallback(async () => {
    const data = await exportPdf(opData?._id, {
      ...opData,
      parcel: parcel && Object.keys(parcel).length ? parcel : null,
    });
    FileDownload(data, `${opData?.code}.pdf`);
  }, [opData, parcel]);

  return (
    <Button onClick={generatePdf} className={classes.pdfButton}>
      <PictureAsPdfIcon />
    </Button>
  );
}

type Props = {
  classes: any;
  shippingRecord: ShippingRecord | null;
  isPublic?: boolean;
  onRemoveShipment?: (shipmentId: string, opId: string) => Promise<void>;
};

function ShippingParcelInfoBlock({
  shippingRecord,
  classes,
  isPublic = false,
  onRemoveShipment,
}: Props) {
  const localClasses = useStyles();
  const { jclasses } = useJStyles();
  const [removingShipmentId, setRemovingShipmentId] = useState<string | null>(
    null
  );
  const [shipmentToRemove, setShipmentToRemove] = useState<Shipment | null>(
    null
  );

  const canRemoveShipment =
    !isPublic && shippingRecord?.status === ShippingRecordStatus.TO_SHIP;

  const handleRemoveShipment = useCallback(
    async (shipment: Shipment) => {
      if (
        !canRemoveShipment ||
        !shipment.id ||
        !shipment.opId ||
        !onRemoveShipment
      ) {
        return;
      }
      setRemovingShipmentId(shipment.id);
      try {
        await onRemoveShipment(shipment.id, shipment.opId);
        setShipmentToRemove(null);
      } finally {
        setRemovingShipmentId(null);
      }
    },
    [canRemoveShipment, onRemoveShipment]
  );

  const handleConfirmRemoveShipment = useCallback(() => {
    if (shipmentToRemove) {
      handleRemoveShipment(shipmentToRemove);
    }
  }, [handleRemoveShipment, shipmentToRemove]);

  // 26/02: this memo doesnt work
  // this is a slow component who don't ever need rerender so we memoize it.
  // const MemoPdfButton = memo(({ shipment }: any) => {
  //   // eslint-disable-next-line react/jsx-no-useless-fragment
  //   return <>{!isPublic && <PDFButton opData={shipment?.op} />}</>;
  // });

  const columns = useMemo(
    () => [
      {
        Header: "Entreprise",
        minWidth: 90,
        maxWidth: 90,
        Cell: ({
          row: { original: shipment },
        }: {
          row: { original: Shipment };
        }) => {
          return (
            <Text type="important" className={classes.cell}>
              {shipment?.op?.company?.name}
            </Text>
          );
        },
      },
      {
        Header: "REF PREPA ENTREPÔT",
        minWidth: 120,
        maxWidth: 120,
        Cell: ({
          row: { original: shipment },
        }: {
          row: { original: Shipment };
        }) => (
          <Text type="important" className={classes.cell}>
            {shipment?.refPreparation || "N/A"}
          </Text>
        ),
      },
      {
        Header: "N° OP",
        minWidth: 80,
        maxWidth: 80,
        Cell: ({
          row: { original: shipment },
        }: {
          row: { original: Shipment };
        }) => (
          <div className={classes.cellContainer}>
            <Text type="important" className={classes.cell}>
              {shipment?.op?.code || PreparationOrderErrors.OP_NOT_FOUND}
            </Text>
            <PDFButton opData={shipment?.op} />
          </div>
        ), //
      },
      {
        Header: "Mètre plancher linéaire",
        minWidth: 100,
        maxWidth: 100,
        Cell: ({
          row: { original: shipment },
        }: {
          row: { original: Shipment };
        }) => (
          <Text type="important" className={classes.cell}>
            {shipment?.parcel?.loadingMeters}
          </Text>
        ),
      },
      {
        Header: "Hauteur (cm)",
        minWidth: 100,
        maxWidth: 100,
        Cell: ({
          row: { original: shipment },
        }: {
          row: { original: Shipment };
        }) => (
          <Text type="important" className={classes.cell}>
            {shipment?.parcel?.height}
          </Text>
        ),
      },
      {
        Header: "Quantité",
        minWidth: 70,
        maxWidth: 70,
        Cell: ({
          row: { original: shipment },
        }: {
          row: { original: Shipment };
        }) => (
          <Text type="important" className={classes.cell}>
            {shipment?.parcel?.quantity}
          </Text>
        ),
      },
      {
        Header: "Poids total (kg)",
        minWidth: 70,
        maxWidth: 70,
        Cell: ({
          row: { original: shipment },
        }: {
          row: { original: Shipment };
        }) => (
          <Text type="important" className={classes.cell}>
            {shipment?.parcel?.weight}
          </Text>
        ),
      },
      {
        Header: "",
        accessor: "expander",
        minWidth: 0,
        maxWidth: 0,
        preventClickAction: true,
        Cell: ({ row }: any) => (
          <div
            style={{
              display: "flex",
              width: "100%",
              justifyContent: "flex-end",
              alignItems: "center",
              cursor: "pointer",
            }}
          >
            <span
              onClick={row.getToggleExpandedHandler()}
              style={{
                minWidth: 30,
                padding: 3,
                marginLeft: 4,
                position: "relative",
                top: 2,
              }}
            >
              {row.getIsExpanded() ? (
                <ArrowUpSLineIcon />
              ) : (
                <ArrowDownSLineIcon />
              )}
            </span>
            {!isPublic &&
              (shippingRecord?.shippingRecordDisputes?.findIndex(
                (dispute) =>
                  dispute.linkedEntityId === row.original.opId &&
                  !dispute.closedAt
              ) !== -1 ? (
                <SentimentVeryDissatisfiedIcon style={{ color: "tomato" }} />
              ) : (
                <SentimentSatisfiedAltIcon style={{ color: "green" }} />
              ))}
          </div>
        ),
      },
      {
        Header: "",
        accessor: "remove",
        minWidth: 50,
        maxWidth: 50,
        preventClickAction: true,
        Cell: ({
          row: { original: shipment },
        }: {
          row: { original: Shipment };
        }) => {
          if (isPublic) {
            return null;
          }
          const isRemoving = removingShipmentId === shipment.id;
          return (
            <Button
              color="primary"
              variant="text"
              className={localClasses.deleteButton}
              style={{ minWidth: 50 }}
              disabled={!canRemoveShipment || isRemoving}
              onClick={() => setShipmentToRemove(shipment)}
            >
              {isRemoving ? (
                <CircularProgress size={20} />
              ) : (
                <DeleteForeverOutlinedIcon />
              )}
            </Button>
          );
        },
      },
    ],
    [
      shippingRecord,
      classes,
      isPublic,
      canRemoveShipment,
      localClasses.deleteButton,
      removingShipmentId,
    ]
  );

  const renderRowSubComponent = useCallback(
    ({ row }) => {
      const op: PreparationOrder = row.original;
      return (
        <div className={classes.subRow}>
          <div className={classes.parcelTable}>
            <ParcelTable parcel={op.parcel} showDetailsTable />
          </div>
        </div>
      );
    },
    [classes]
  );

  return (
    <InfoContainer
      className={classes.paperContainer}
      leftHeader={
        <div className={classes.infoHeader}>
          <Icon size={1} path={mdiPackageVariantClosed} />
          <Text type="h3">Informations colisage</Text>
        </div>
      }
    >
      <div className={classes.parcelTableContainer}>
        <JTable
          data={shippingRecord?.shipments || []}
          columns={columns}
          rowProps={({
            original: currentOP,
          }: {
            original: PreparationOrder & { disabled: boolean };
          }) => {
            const rowProps: any = {
              key: currentOP._id,
              disabled: currentOP.disabled,
              canExpand: !currentOP.disabled,
              style: { cursor: "default" },
            };
            return rowProps;
          }}
          renderRowSubComponent={renderRowSubComponent}
          hideFilters
          hideCheckboxes
          setFilter={() => {}}
          setPageIndex={() => {}}
          pageCount={1}
          pageIndex={0}
        />
      </div>
      <div className={classes.totalParcelContainer}>
        <div className={classes.totalParcelTitle}>COLISAGE TOTAL</div>
        <TableContainer className={classes.totalParcelTable}>
          <TableHead className={classes.tableHead}>
            <TableCell width={150} className={classes.opTableHeadCell}>
              Mètre plancher
            </TableCell>
            <TableCell width={150} className={classes.opTableHeadCell}>
              Hauteur (cm)
            </TableCell>
            <TableCell width={150} className={classes.opTableHeadCell}>
              Quantité
            </TableCell>
            <TableCell width={150} className={classes.opTableHeadCell}>
              Poids total (kg)
            </TableCell>
          </TableHead>
          <TableBody>
            <TableCell className={classes.cell}>
              <NumericFormat
                displayType="text"
                decimalScale={2}
                value={shippingRecord?.totalParcel?.loadingMeters}
              />
            </TableCell>
            <TableCell className={classes.cell}>
              {shippingRecord?.totalParcel?.height}
            </TableCell>
            <TableCell className={classes.cell}>
              {shippingRecord?.totalParcel?.quantity}
            </TableCell>
            <TableCell className={classes.cell}>
              {shippingRecord?.totalParcel?.weight}
            </TableCell>
          </TableBody>
        </TableContainer>
      </div>
      <Modal
        title="Confirmation de suppression"
        textValidate="Oui"
        textCancel="Non"
        open={!!shipmentToRemove}
        isLoading={!!removingShipmentId}
        disableExternalClosing={!!removingShipmentId}
        onValidate={handleConfirmRemoveShipment}
        onCancel={() => setShipmentToRemove(null)}
      >
        <div className={jclasses.dialogContent} style={{ textAlign: "center" }}>
          <Text>{`Êtes vous sur de vouloir supprimer l'OP ?`}</Text>
        </div>
      </Modal>
    </InfoContainer>
  );
}

export default ShippingParcelInfoBlock;

const useStyles = createUseStyles(() => ({
  pdfButton: {
    border: "none !important",
    color: "rgba(0, 0, 0) !important",
    "&:hover": {
      color: "rgba(0, 0, 0) !important",
      backgroundColor: "rgba(0, 0, 0, 0.04) !important",
    },
    "&:disabled": {
      color: "rgba(0, 0, 0, 0.26) !important",
    },
    "& svg": {
      color: "inherit !important",
    },
  },
  deleteButton: {
    color: "rgba(0, 0, 0) !important",
    border: "none !important",
    "&:hover": {
      color: "rgba(0, 0, 0) !important",
      backgroundColor: "rgba(0, 0, 0, 0.04) !important",
    },
    "&:disabled": {
      color: "rgba(0, 0, 0, 0.26) !important",
    },
    "& svg": {
      color: "inherit !important",
    },
  },
}));
